import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import helpSupportService from '../../services/helpSupportService';

export const fetchTickets = createAsyncThunk('helpSupport/fetchTickets', async (params, { rejectWithValue }) => {
  try {
    const response = await helpSupportService.getAllTickets(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch tickets');
  }
});

export const fetchTicketById = createAsyncThunk('helpSupport/fetchTicketById', async (id, { rejectWithValue }) => {
  try {
    const response = await helpSupportService.getTicketById(id);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch ticket');
  }
});

export const addResponse = createAsyncThunk('helpSupport/addResponse', async ({ ticketId, data }, { rejectWithValue }) => {
  try {
    const response = await helpSupportService.addResponse(ticketId, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to add response');
  }
});

export const updateTicketStatus = createAsyncThunk('helpSupport/updateStatus', async ({ ticketId, data }, { rejectWithValue }) => {
  try {
    const response = await helpSupportService.updateStatus(ticketId, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to update status');
  }
});

export const fetchStats = createAsyncThunk('helpSupport/fetchStats', async (_, { rejectWithValue }) => {
  try {
    const response = await helpSupportService.getStats();
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch stats');
  }
});

const helpSupportSlice = createSlice({
  name: 'helpSupport',
  initialState: {
    tickets: [],
    currentTicket: null,
    stats: null,
    totalTickets: 0,
    totalPages: 0,
    isLoading: false,
    success: false,
    error: null,
  },
  reducers: {
    resetSuccess: (state) => {
      state.success = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentTicket: (state) => {
      state.currentTicket = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTickets.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchTickets.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tickets = action.payload.data;
        state.totalTickets = action.payload.pagination?.totalItems || 0;
        state.totalPages = action.payload.pagination?.totalPages || 0;
      })
      .addCase(fetchTickets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchTicketById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchTicketById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentTicket = action.payload.data;
      })
      .addCase(fetchTicketById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addResponse.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addResponse.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentTicket = action.payload.data;
        state.success = true;
      })
      .addCase(addResponse.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateTicketStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateTicketStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentTicket = action.payload.data;
        const index = state.tickets.findIndex((t) => t._id === action.payload.data._id);
        if (index !== -1) {
          state.tickets[index] = action.payload.data;
        }
        state.success = true;
      })
      .addCase(updateTicketStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchStats.fulfilled, (state, action) => {
        state.stats = action.payload.data;
      });
  },
});

export const { resetSuccess, clearError, clearCurrentTicket } = helpSupportSlice.actions;
export default helpSupportSlice.reducer;
